import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import ProfileConfirm from "../profiles/ProfileConfirm";
import ProfileQA from "../profiles/AuditQA";
import PostRate from "../posts/PostRate";
import ReportsSummary from "../reports/ReportsSummary";
import {
  PostTypeFilter,
  ReportSummaryItemApi,
  REPORT_STATUS_ENUM,
  SourceTypeFilter,
  REPORT_TYPES,
} from "../api/Reports";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useGetSubworkflowData,
  useGetWorkflowRunByUUID,
  useUpdateMisconductDueDate,
} from "../api/WorkflowApi";
import "react-day-picker/dist/style.css";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import SystemRate from "../system/SystemRate";
import Amendment from "../amendment/Amendment";
import { useReportUnlock } from "./useReportUnlock";
import {
  WORKFLOW_STATUS_ENUM,
  WORKFLOW_STEP_STATUS_ENUM,
  WorkflowRun,
} from "../api/Workflows";

import { FlagCountResponse, GroupCount, TopicCount } from "../api/Items";
import { useBlocker } from "../utils/blocker";
import { Transition } from "history";
import ReportUnlockHook from "./ReportUnlockHook";
import { SummaryFilterTypeEnum } from "./ReportSidebar/SidebarSummary";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import ManualScrape from "../manual-scrape/ManualScrape";
import { parseISO } from "date-fns";
import WebContent from "../webcontent/WebContent";
import {
  ReportDetailProvider,
  useReportDetail,
  useReportTimer,
} from "./ReportDetailProvider";
import { isReportLockedByOther, ReportDetailTypes } from "./utils";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import PostDetails from "../amendment/PostDetails";
import { useActorInfoQuery } from "../api/UsersApi";
import { useLockModal, useReportOriginManager } from "./ReportsHooks";
import { useFamaFlags } from "../api/FamaFlags";
import ReportDetailBase from "./ReportDetailBase";
import ReportDetail360 from "./ReportDetail360";
import { get360SubworkflowRunUuid } from "../360/reportAssignment";
import { useGetCompanyBase } from "../api/CompaniesApi";

const ReportDetail = ({ summaryOnly }: { summaryOnly?: boolean }) => {
  const { id } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const is360MisconductQaRoute = pathname === `/360/${id}/misconduct-qa`;
  const locationIsReadOnly = (location.state as { isReadOnly?: boolean })
    ?.isReadOnly;
  const navigate = useNavigate();
  const { setOrigin, getOrigin } = useReportOriginManager();
  const { lock } = useLockModal();
  const { data: actorInfo, isLoading: isActorInfoLoading } =
    useActorInfoQuery(true);

  const {
    report,
    errorLoadingReport,
    isReportForbidden,
    isSuccessLoadingReport,
    isSuccessLoadingBlueprintAnnotated,
    errorBlueprint,
    blueprint,
  } = useReportDetail();
  const {
    canChangeDueDate,
    canPostScrape,
    canConfirmProfile,
    canPostRate,
    canNavAmendment,
    canAddNotes,
    canInternalQaEdit,
  } = usePermissionValidator();
  const { useStatsInSummary } = useFamaFlags();

  const {
    data: company,
    mutate: getCompanyBase,
    isLoading: companyLoading,
    isSuccess: companyFetchSucceeded,
    variables: fetchedCompanyUUID,
  } = useGetCompanyBase();

  const {
    data: workflowRun,
    error: errorWorkflowRun,
    isLoading: isLoadingWorkflowRun,
  } = useGetWorkflowRunByUUID(report?.workflow_run_uuid);
  const { mutateAsync: subworkflowDataGet } = useGetSubworkflowData();

  const [flagCount, setFlagCount] = useState<FlagCountResponse>();
  const [keywordCount, setKeywordCount] = useState<GroupCount>();
  const [topicCount, setTopicCount] = useState<TopicCount>();
  const [isDueDateModalOpen, setIsDueDateModalOpen] = useState(false);
  const [isDueDateErrorModalOpen, setIsDueDateErrorModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();

  const [sourceTypeFilter, setSourceTypeFilter] = useState<SourceTypeFilter>({
    facebook: false,
    twitter: false,
    instagram: false,
    web: false,
  });
  const [postTypeFilter, setPostTypeFilter] = useState<PostTypeFilter>({
    original: false,
    shared: false,
    liked: false,
  });

  const [behaviorFilter, setBehaviorFilter] = useState<string[]>([]);
  const [keywordFilter, setKeywordFilter] = useState<string[]>([]);
  const [topicFilter, setTopicFilter] = useState<string[]>([]);
  const [itemTypeFilter, setItemTypeFilter] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState({
    fromDate: "",
    toDate: "",
  });
  const [subStep, setSubStep] = useState<string>("profiles");
  const [reportSummary, setReportSummary] = useState<ReportSummaryItemApi>();
  const [reportOrigin, setReportOrigin] = useState<string>("none");
  const [threeSixtySubworkflowData, setThreeSixtySubworkflowData] = useState<{
    parentWorkflowRunUuid: string;
    children: WorkflowRun[];
  }>();
  const originCapturedForReportRef = useRef<string | null>(null);
  const lockCalledForReportRef = useRef<string | null>(null);

  const companyUUID = reportSummary?.company_uuid;

  useEffect(() => {
    if (companyUUID) {
      getCompanyBase(companyUUID);
    }
  }, [companyUUID, getCompanyBase]);

  const companyMatchesReport =
    !!companyUUID &&
    fetchedCompanyUUID === companyUUID &&
    !companyLoading &&
    companyFetchSucceeded;
  const companyNote = companyMatchesReport ? company?.notes : undefined;

  useEffect(() => {
    if (!is360MisconductQaRoute || !report?.workflow_run_uuid) {
      setThreeSixtySubworkflowData(undefined);
      return;
    }

    let isCurrentRequest = true;
    const parentWorkflowRunUuid = report.workflow_run_uuid;
    setThreeSixtySubworkflowData(undefined);
    subworkflowDataGet(parentWorkflowRunUuid)
      .then((children) => {
        if (!isCurrentRequest) return;
        setThreeSixtySubworkflowData({
          parentWorkflowRunUuid,
          children,
        });
      })
      .catch(() => {
        if (!isCurrentRequest) return;
        setThreeSixtySubworkflowData(undefined);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [is360MisconductQaRoute, report?.workflow_run_uuid, subworkflowDataGet]);

  const threeSixtyWorkflowRunUuid = useMemo(() => {
    if (
      !is360MisconductQaRoute ||
      !report ||
      !threeSixtySubworkflowData ||
      threeSixtySubworkflowData.parentWorkflowRunUuid !==
        report.workflow_run_uuid
    ) {
      return undefined;
    }
    return get360SubworkflowRunUuid(threeSixtySubworkflowData.children);
  }, [is360MisconductQaRoute, report, threeSixtySubworkflowData]);

  const reportLockWorkflowRunUuid = is360MisconductQaRoute
    ? threeSixtyWorkflowRunUuid ?? null
    : report?.workflow_run_uuid ?? null;

  const {
    performUnlock,
    error: errorUnlockingReport,
    modal: modalUnlock,
  } = useReportUnlock();
  const {
    data: workflowUpdated,
    mutate: updateWorkflowDueDate,
    error: getWorkflowDueDateError,
  } = useUpdateMisconductDueDate();

  const handleDueDateChange = async (newDueDate: Date) => {
    if (reportSummary) {
      setIsDueDateModalOpen(false);
      updateWorkflowDueDate({
        external_id: reportSummary.workflow_run_uuid,
        due_date: newDueDate,
      });
      // WARNING
      // we need to clear the state so report summary information is queried in case of reload.
      window.history.replaceState({}, "");
    }
  };

  const getCurrentType = useCallback((): ReportDetailTypes => {
    if (
      pathname === `/reports/${id}/detail` ||
      pathname === `/reports/${id}/amendment/detail`
    ) {
      return ReportDetailTypes.PROFILE_CONFIRM;
    }

    if (pathname === `/reports/${id}/qa`) {
      return ReportDetailTypes.PROFILE_QA;
    }

    if (
      pathname === `/reports/${id}/iqa` ||
      pathname === `/reports/${id}/cqa` ||
      pathname === `/reports/${id}/aqa`
    ) {
      return ReportDetailTypes.PROFILE_QA;
    }

    if (pathname === `/360/${id}/misconduct-qa`) {
      return ReportDetailTypes.PROFILE_QA;
    }

    if (
      pathname === `/reports/${id}/post-rate` ||
      pathname === `/reports/${id}/amendment/post-rate`
    ) {
      return ReportDetailTypes.MANUAL_RATE;
    }

    if (
      pathname === `/reports/${id}/system` ||
      pathname === `/reports/${id}/amendment/system`
    ) {
      return ReportDetailTypes.SYSTEM;
    }

    if (pathname === `/reports/${id}/summary` || summaryOnly) {
      return ReportDetailTypes.SUMMARY;
    }
    if (
      pathname === `/reports/${id}/scrape` ||
      pathname === `/reports/${id}/amendment/scrape`
    ) {
      return ReportDetailTypes.SCRAPE;
    }
    if (
      pathname === `/reports/${id}/webcontent` ||
      pathname === `/reports/${id}/amendment/article-edit`
    ) {
      return ReportDetailTypes.WEB_CONTENT;
    }

    if (pathname === `/reports/${id}/amendment/post-edit`) {
      return ReportDetailTypes.POST_EDIT;
    }
    return ReportDetailTypes.INVALID;
  }, [id, pathname, summaryOnly]);

  const currentType = useMemo(() => getCurrentType(), [getCurrentType]);
  const isAmendment =
    pathname.includes("amendment") || pathname.includes("aqa");

  function getDetailTitle() {
    if (isAmendment) {
      return "Amendment";
    }
    if (currentType === ReportDetailTypes.SUMMARY) {
      return "Report Summary";
    }

    if (currentType === ReportDetailTypes.SCRAPE) {
      return "Manual Profile Scrape";
    }

    if (currentType === ReportDetailTypes.WEB_CONTENT) {
      return "Web Content";
    }

    if (!!blueprint && currentType === ReportDetailTypes.PROFILE_QA) {
      const is360Report = blueprint?.report_type === REPORT_TYPES.THREE_SIXTY;
      return is360Report ? "360 Misconduct Report Review" : "Quality Audit";
    }

    return "Report Detail";
  }

  const isReadOnlyReport = () => {
    if (locationIsReadOnly) return true;
    // Opening from In Process with isReadOnly: false can still see is_locked=true (WS/cache race).
    // Don't treat that alone as read-only.
    if (
      locationIsReadOnly !== false &&
      isReportLockedByOther(reportSummary, actorInfo?.actor_uuid)
    ) {
      return true;
    }

    const type = getCurrentType();

    // Check permission for 360 misconduct QA
    if (pathname === `/360/${id}/misconduct-qa` && !canInternalQaEdit) {
      return true;
    }

    const typeMapping = {
      PROFILE_CONFIRM: !canConfirmProfile,
      MANUAL_RATE: !canPostRate,
      SCRAPE: !canPostScrape,
      SYSTEM: !canPostRate,
      POST_EDIT: !canNavAmendment,
    };

    // @ts-ignore
    return typeMapping[type];
  };

  const isReadOnly = isReadOnlyReport();

  useBlocker((tx: Transition) =>
    ReportUnlockHook(
      tx,
      isReadOnly,
      reportSummary,
      id,
      getCurrentType,
      performUnlock,
      !!errorUnlockingReport,
      reportLockWorkflowRunUuid
    )
  );

  useEffect(() => {
    if (report) {
      setReportSummary(report);

      // Capture origin only once per report to handle "consume once" pattern
      // Reset when navigating to a different report
      let origin: string;
      if (originCapturedForReportRef.current !== report.report_uuid) {
        origin = getOrigin();
        setReportOrigin(origin);
        originCapturedForReportRef.current = report.report_uuid;
      } else {
        origin = reportOrigin;
      }

      if (isReadOnly || currentType === ReportDetailTypes.SUMMARY) return;

      let effectiveOrigin = origin;
      if (effectiveOrigin === "none") {
        setOrigin("direct");
        effectiveOrigin = "direct";
      }

      // Prevent duplicate lock calls for the same report
      if (lockCalledForReportRef.current === report.report_uuid) return;

      if (
        effectiveOrigin === "direct" ||
        effectiveOrigin === "task_assignment"
      ) {
        if (!reportLockWorkflowRunUuid) return;
        lockCalledForReportRef.current = report.report_uuid;
        lock(reportLockWorkflowRunUuid, effectiveOrigin).catch(() => {
          // Error is handled by react-query's error state, no action needed
        });
      }
    }
  }, [
    getOrigin,
    lock,
    report,
    setOrigin,
    isReadOnly,
    currentType,
    reportOrigin,
    reportLockWorkflowRunUuid,
  ]);

  useEffect(() => {
    if (!workflowUpdated) return;
    setSelectedDate(parseISO(workflowUpdated));
  }, [workflowUpdated]);

  const { resetTimer } = useReportTimer();

  useEffect(() => {
    if (
      isSuccessLoadingBlueprintAnnotated &&
      isSuccessLoadingReport &&
      currentType !== ReportDetailTypes.SUMMARY
    ) {
      resetTimer?.();
    }
  }, [
    isSuccessLoadingBlueprintAnnotated,
    isSuccessLoadingReport,
    resetTimer,
    currentType,
  ]);

  const sourceFilterApplied = (
    event: React.ChangeEvent<HTMLInputElement>,
    source: keyof SourceTypeFilter
  ) => {
    let newFilter: SourceTypeFilter = { ...sourceTypeFilter };
    newFilter[source] = event.target.checked;
    setSourceTypeFilter(newFilter);
  };

  const postTypeFilterApplied = (
    event: React.ChangeEvent<HTMLInputElement>,
    source: keyof PostTypeFilter
  ) => {
    let newFilter: PostTypeFilter = { ...postTypeFilter };
    newFilter[source] = event.target.checked;
    setPostTypeFilter(newFilter);
  };

  const reviewIsInProgress = () => {
    return currentType !== ReportDetailTypes.SUMMARY;
  };

  const behaviorFilterApplied = (
    event: React.ChangeEvent<HTMLInputElement>,
    behavior: string
  ) => {
    let newFilter = [...behaviorFilter];
    if (!newFilter.includes(behavior) && event.target.checked) {
      newFilter.push(behavior);
      setBehaviorFilter(newFilter);
    }
    if (newFilter.includes(behavior) && !event.target.checked) {
      newFilter = newFilter.filter((element) => element !== behavior);
      setBehaviorFilter(newFilter);
    }
  };

  const keywordFilterApplied = (
    event: React.ChangeEvent<HTMLInputElement>,
    keyword: string
  ) => {
    const checked = event.target.checked;
    setKeywordFilter((current) => {
      const normalized = keyword.toLowerCase();
      const withoutKeyword = current.filter(
        (element) => element.toLowerCase() !== normalized
      );
      return checked ? [...withoutKeyword, keyword] : withoutKeyword;
    });
  };

  const topicFilterApplied = (
    event: React.ChangeEvent<HTMLInputElement>,
    topic: string
  ) => {
    const checked = event.target.checked;
    setTopicFilter((current) => {
      const normalized = topic.toLowerCase();
      const withoutTopic = current.filter(
        (element) => element.toLowerCase() !== normalized
      );
      return checked ? [...withoutTopic, topic] : withoutTopic;
    });
  };

  const itemTypeFilterApplied = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    let newFilter = [...itemTypeFilter];
    if (!newFilter.includes(type) && event.target.checked) {
      newFilter.push(type);
      setItemTypeFilter(newFilter);
    }
    if (newFilter.includes(type) && !event.target.checked) {
      newFilter = newFilter.filter((element) => element !== type);
      setItemTypeFilter(newFilter);
    }
  };

  const dateFilterApplied = (
    event: React.ChangeEvent<HTMLInputElement>,
    source: string
  ) => {
    if (source === "fromDate")
      setDateFilter({ ...dateFilter, fromDate: event.target.value });

    if (source === "toDate")
      setDateFilter({ ...dateFilter, toDate: event.target.value });
  };

  function resetFilter() {
    setBehaviorFilter([]);
    setKeywordFilter([]);
    setTopicFilter([]);
    setItemTypeFilter([]);
    setSourceTypeFilter({
      facebook: false,
      twitter: false,
      instagram: false,
      web: false,
    });
  }

  const isFilterEmpty =
    behaviorFilter.length === 0 &&
    keywordFilter.length === 0 &&
    topicFilter.length === 0 &&
    itemTypeFilter.length === 0 &&
    dateFilter.fromDate === "" &&
    dateFilter.toDate === "" &&
    Object.values(sourceTypeFilter).every((value) => !value) &&
    Object.values(postTypeFilter).every((value) => !value);

  function applyFilterByType(
    type: SummaryFilterTypeEnum,
    event: React.ChangeEvent<HTMLInputElement> | null,
    source: string
  ) {
    switch (type) {
      case SummaryFilterTypeEnum.source:
        return (
          event && sourceFilterApplied(event, source as keyof SourceTypeFilter)
        );
      case SummaryFilterTypeEnum.postType:
        return (
          event && postTypeFilterApplied(event, source as keyof PostTypeFilter)
        );
      case SummaryFilterTypeEnum.behavior:
        return event && behaviorFilterApplied(event, source);
      case SummaryFilterTypeEnum.keyword:
        return event && keywordFilterApplied(event, source);
      case SummaryFilterTypeEnum.topic:
        return event && topicFilterApplied(event, source);
      case SummaryFilterTypeEnum.itemType:
        return event && itemTypeFilterApplied(event, source);
      case SummaryFilterTypeEnum.date:
        return event && dateFilterApplied(event, source);
      case SummaryFilterTypeEnum.reset:
        return resetFilter();
    }
  }

  const exitHandler = useCallback(() => {
    if (
      currentType === ReportDetailTypes.PROFILE_QA &&
      report?.step_name === WORKFLOW_STEP_STATUS_ENUM.InternalQA
    ) {
      navigate("/reports/internal_qa");
    } else if (
      currentType === ReportDetailTypes.PROFILE_QA &&
      report?.step_name === WORKFLOW_STEP_STATUS_ENUM.ClientQA
    ) {
      navigate("/reports/client_qa");
    } else if (
      currentType === ReportDetailTypes.PROFILE_QA &&
      report?.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyQA
    ) {
      navigate("/360/qa");
    } else if (
      currentType === ReportDetailTypes.PROFILE_QA &&
      report?.step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyPlusReview
    ) {
      navigate("/360/investigating");
    } else if (isAmendment) {
      navigate("/reports/amendment");
    } else {
      navigate("/reports");
    }
  }, [currentType, report, navigate, isAmendment]);

  function updateFlagCount(newFlags: FlagCountResponse) {
    setFlagCount(newFlags);
  }

  function updateKeywordCount(newKeywords: GroupCount) {
    setKeywordCount(newKeywords);
  }

  function updateTopicCount(newTopics: TopicCount) {
    setTopicCount(newTopics);
  }

  const showChangeDueDateButton = (): boolean => {
    if (!reportSummary) return false;
    if (!canChangeDueDate) return false;
    if (reportSummary.report_status === REPORT_STATUS_ENUM.AMENDMENT)
      return false;
    const validStatus: string[] = [
      WORKFLOW_STATUS_ENUM.TO_DO,
      WORKFLOW_STATUS_ENUM.IN_PROGRESS,
    ];
    return validStatus.includes(reportSummary.workflow_status);
  };

  const REPORT_DETAILS: { [key in ReportDetailTypes]: JSX.Element } =
    useMemo(() => {
      if (!reportSummary)
        return {
          INVALID: <></>,
          PROFILE_CONFIRM: <></>,
          PROFILE_QA: <></>,
          MANUAL_RATE: <></>,
          SYSTEM: <></>,
          AMENDMENT: <></>,
          SCRAPE: <></>,
          WEB_CONTENT: <></>,
          POST_EDIT: <></>,
          SUMMARY: <></>,
        };
      return {
        PROFILE_CONFIRM: (
          <ProfileConfirm
            isReadOnly={isReadOnly}
            report={reportSummary}
            handleExit={exitHandler}
            isAmendment={isAmendment}
          />
        ),
        PROFILE_QA: (
          <ProfileQA
            report={reportSummary}
            handleExit={exitHandler}
            updateFlagCount={updateFlagCount}
            updateKeywordCount={updateKeywordCount}
            sourceTypeFilter={sourceTypeFilter}
            postTypeFilter={postTypeFilter}
            dateFilter={dateFilter}
            behaviorFilter={behaviorFilter}
            keywordFilter={keywordFilter}
            topicFilter={topicFilter}
            itemTypeFilter={itemTypeFilter}
            updateQAStep={setSubStep}
            subStep={subStep}
            workflowRun={workflowRun}
            isLoadingWorkflowRun={isLoadingWorkflowRun}
            errorWorkflowRun={!!errorWorkflowRun}
          />
        ),
        MANUAL_RATE: (
          <PostRate
            isReadOnly={isReadOnly}
            report={reportSummary}
            handleExit={exitHandler}
            isAmendment={isAmendment}
          />
        ),
        SUMMARY: (
          <ReportsSummary
            updateFlagCount={updateFlagCount}
            updateKeywordCount={updateKeywordCount}
            updateTopicCount={updateTopicCount}
            sourceTypeFilter={sourceTypeFilter}
            postTypeFilter={postTypeFilter}
            dateFilter={dateFilter}
            behaviorFilter={behaviorFilter}
            keywordFilter={keywordFilter}
            topicFilter={topicFilter}
            itemTypeFilter={itemTypeFilter}
            report={reportSummary}
            summaryOnly={summaryOnly}
          />
        ),
        SYSTEM: (
          <SystemRate
            isReadOnly={isReadOnly}
            handleExit={exitHandler}
            report={reportSummary}
            isAmendment={isAmendment}
          />
        ),
        AMENDMENT: <Amendment isReadOnly={isReadOnly} report={reportSummary} />,
        SCRAPE: (
          <ManualScrape
            isReadOnly={isReadOnly}
            report={reportSummary}
            handleExit={exitHandler}
            isAmendment={isAmendment}
          />
        ),
        WEB_CONTENT: (
          <WebContent
            isReadOnly={isReadOnly}
            report={reportSummary}
            dateFilter={dateFilter}
            handleExit={exitHandler}
            isAmendment={isAmendment}
          ></WebContent>
        ),
        POST_EDIT: (
          <PostDetails
            report={reportSummary}
            sourceTypeFilter={sourceTypeFilter}
            postTypeFilter={postTypeFilter}
            dateFilter={dateFilter}
            behaviorFilter={behaviorFilter}
            keywordFilter={keywordFilter}
            topicFilter={topicFilter}
            isReadOnly={isReadOnly}
          />
        ),
        INVALID: <></>,
      };
    }, [
      reportSummary,
      isReadOnly,
      exitHandler,
      isAmendment,
      sourceTypeFilter,
      errorWorkflowRun,
      isLoadingWorkflowRun,
      workflowRun,
      postTypeFilter,
      dateFilter,
      behaviorFilter,
      keywordFilter,
      topicFilter,
      itemTypeFilter,
      subStep,
      summaryOnly,
    ]);

  if (isReportForbidden) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (errorLoadingReport) {
    return <ErrorMessageContainer message="Error while loading the report" />;
  }

  if (errorUnlockingReport) {
    return <ErrorMessageContainer message="Error Unlocking Report" />;
  }

  if (!reportSummary) return <LoadingSpinner />;

  // Without explicit navigation intent, the lock-based read-only check needs
  // the current actor to tell a self-held lock from a foreign one. Wait for
  // actor identity instead of misclassifying a self-held lock as read-only.
  if (
    isActorInfoLoading &&
    locationIsReadOnly === undefined &&
    !!reportSummary.is_locked
  ) {
    return <LoadingSpinner />;
  }

  // Use 360 layout only for 360 reports that are NOT coming from In Process page
  const shouldUse360Layout =
    blueprint?.report_type === REPORT_TYPES.THREE_SIXTY &&
    reportOrigin !== "in_process";

  if (shouldUse360Layout) {
    return (
      <ReportDetail360
        reportSummary={reportSummary}
        summaryOnly={summaryOnly}
        isReadOnly={isReadOnly}
        reviewIsInProgress={reviewIsInProgress}
        applyFilterByType={applyFilterByType}
        flagCount={flagCount}
        keywordCount={keywordCount}
        topicCount={topicCount}
        keywordFilter={keywordFilter}
        topicFilter={topicFilter}
        subStep={subStep}
        isFilterEmpty={isFilterEmpty}
        dateFilter={dateFilter}
        blueprint={blueprint}
        errorBlueprint={errorBlueprint}
        currentType={currentType}
        modalUnlock={modalUnlock}
        canAddNotes={canAddNotes}
        companyNote={companyNote}
        REPORT_DETAILS={REPORT_DETAILS}
      />
    );
  }
  return (
    <ReportDetailBase
      reportSummary={reportSummary}
      summaryOnly={summaryOnly}
      getDetailTitle={getDetailTitle}
      useStatsInSummary={useStatsInSummary}
      report={report}
      isReadOnly={isReadOnly}
      reviewIsInProgress={reviewIsInProgress}
      canChangeDueDate={canChangeDueDate}
      selectedDate={selectedDate}
      handleDueDateChange={handleDueDateChange}
      applyFilterByType={applyFilterByType}
      flagCount={flagCount}
      keywordCount={keywordCount}
      topicCount={topicCount}
      keywordFilter={keywordFilter}
      topicFilter={topicFilter}
      subStep={subStep}
      isFilterEmpty={isFilterEmpty}
      dateFilter={dateFilter}
      blueprint={blueprint}
      errorBlueprint={errorBlueprint}
      currentType={currentType}
      modalUnlock={modalUnlock}
      canAddNotes={canAddNotes}
      companyNote={companyNote}
      showChangeDueDateButton={showChangeDueDateButton}
      setIsDueDateModalOpen={setIsDueDateModalOpen}
      isDueDateModalOpen={isDueDateModalOpen}
      getWorkflowDueDateError={getWorkflowDueDateError}
      isDueDateErrorModalOpen={isDueDateErrorModalOpen}
      setIsDueDateErrorModalOpen={setIsDueDateErrorModalOpen}
      REPORT_DETAILS={REPORT_DETAILS}
    />
  );
};

function ReportDetailWrapper() {
  return (
    <ReportDetailProvider>
      <ReportDetail />
    </ReportDetailProvider>
  );
}

export default ReportDetailWrapper;

export function ReportDetailSummaryOnly() {
  return (
    <ReportDetailProvider>
      <ReportDetail summaryOnly={true} />
    </ReportDetailProvider>
  );
}
